import React, { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { FieldDropdown } from 'src/components/FieldDropdown'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { selectSalesOverview } from 'src/features/receivement/ReceivementSelectors'
import { actions } from 'src/features/receivement/ReceivementState'
import {
  ProductStatusEnum, SalesBatchesValue, SalesDropdownOptions, SalesReceivementBatch,
} from 'src/features/receivement/ReceivementTypes'
import { useSelector } from 'src/utils/store'
import * as S from '../styles'
import { clampAcceptedToAr } from './clampAcceptedToAr'
import { dropdownOptions } from './dropdownOptions'
import { arAcceptCeiling } from './validateAcceptedQuantity'

interface Props {
  batch: SalesReceivementBatch
  salesPerDay: number
  productStatus: ProductStatusEnum;
  invoiceNumber: number;
  idReceivementAuthorization: number;
  handleErrorQuantity: (value: number, option?: string) => void;
  productIsRejected?: boolean;
}

const ActionDropdown = React.memo(({
  batch,
  salesPerDay,
  productStatus,
  productIsRejected,
  invoiceNumber,
  idReceivementAuthorization,
  handleErrorQuantity,
}: Props) => {
  const dispatch = useDispatch()
  const batchState = useSelector((state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')])
  const isProductNotReceived = productStatus === ProductStatusEnum.DIVERGENT && batch.totalQuantity === 0
  const salesOverview = useSelector(selectSalesOverview)
  const ref = React.useRef(false)
  const [selectedAction, setSelectedAction] = useState<SalesDropdownOptions>()
  const [openDecisionConfirmationModal, setOpenDecisionConfirmationModal] = useState(false)
  const closeConfirmationModal = () => setOpenDecisionConfirmationModal(false)

  const batchKey = batch.idsBatchItemOrder.join(',')

  const currentProduct = useMemo(
    () => salesOverview?.products.find(
      (p) => p.batches.some((b) => b.idsBatchItemOrder.join(',') === batchKey),
    ),
    [salesOverview?.products, batchKey],
  )

  // Saldo do teto da AR para este lote = AR - aceito dos demais lotes do produto.
  const siblingAcceptedSum = useSelector((state) => {
    if (!currentProduct) return 0
    return currentProduct.batches.reduce((sum, b) => {
      const key = b.idsBatchItemOrder.join(',')
      if (key === batchKey) return sum
      return sum + (state.receivement.batchesStateByBatchItemOrderIds[key]?.acceptedQuantity || 0)
    }, 0)
  })

  const isKg = currentProduct?.isKg ?? batch.measureUnit?.isKg ?? false
  const remainingArBudget = currentProduct
    ? arAcceptCeiling(currentProduct.expectedQuantity, isKg) - siblingAcceptedSum
    : Infinity

  const options = useMemo(() => {
    let optionsArray = Object.values(dropdownOptions)

    if (salesOverview) {
      if (!salesOverview.allowInvoiceReturn || !salesOverview.allowPartialReturn) {
        optionsArray = optionsArray.filter(
          (o) => o !== dropdownOptions.ACCEPT_ALL && o !== dropdownOptions.PARTIAL && o !== dropdownOptions.ACCEPT_ALL_AND_DAMAGED,
        )
      } else {
        optionsArray = optionsArray.filter(
          (o) => o !== dropdownOptions.ACCEPT_EVERYTHING,
        )

        if (batch.damageQuantity === 0) {
          optionsArray = optionsArray.filter(
            (o) => o !== dropdownOptions.ACCEPT_ALL_AND_DAMAGED,
          )
        }

        if (batch.usefulQuantity === 0) {
          optionsArray = optionsArray.filter(
            (o) => o !== dropdownOptions.ACCEPT_ALL,
          )
        }
      }

      if (!salesOverview.allowPartialReturn || salesOverview.allowBreakUnitOnInvoiceReturn || batch.damageQuantity === 0) {
        optionsArray = optionsArray.filter(
          (o) => o !== dropdownOptions.ACCEPT_MAXIMUM,
        )
      }
    }

    return optionsArray
  }, [salesOverview, batch.damageQuantity, batch.usefulQuantity])

  // Fornecedor tudo ou nada: so tem "Aceitar tudo" / "Rejeitar tudo" (aceita tudo da nota ou rejeita tudo)
  const isAllOrNothingSupplier = !!salesOverview
    && (!salesOverview.allowInvoiceReturn || !salesOverview.allowPartialReturn)

  const handleOption = React.useCallback((event) => {
    const newOption = event.target.value as SalesDropdownOptions

    const isArWideAction = newOption === dropdownOptions.ACCEPT_EVERYTHING
      || (newOption === dropdownOptions.REJECT_ALL && isAllOrNothingSupplier)

    if (isArWideAction) {
      // Igual ao caminho com 2+ produtos (modal): vale para a AR toda. No "Aceitar tudo" o reducer
      // distribui o teto entre os lotes, devolvendo primeiro a avaria e depois a validade mais curta.
      dispatch(actions.setActionForAllProducts({ idReceivementAuthorization, option: newOption }))
      return
    }

    const optionResult: Record<string, () => Partial<SalesBatchesValue>> = {
      [dropdownOptions.ACCEPT_ALL]: () => {
        // "Aceitar sem avarias" aceita todo o util sem clampar; se ficar acima
        // da AR, a validacao do produto exibe erro (igual a entrada manual).
        const accepted = batch.usefulQuantity
        return {
          acceptedQuantity: accepted,
          returnQuantity: batch.totalQuantity - accepted,
          stockDays: salesPerDay !== 0 ? accepted / salesPerDay : 0,
          option: 'ACCEPT_ALL',
        }
      },

      [dropdownOptions.ACCEPT_ALL_AND_DAMAGED]: () => {
        // Aceita todo o recebido (util + avaria), sem clampar na AR.
        const accepted = batch.usefulQuantity + batch.damageQuantity
        return {
          acceptedQuantity: accepted,
          returnQuantity: undefined,
          option: 'ACCEPT_ALL_AND_DAMAGED',
          stockDays: salesPerDay !== 0 ? accepted / salesPerDay : 0,
          acceptDamage: true,
        }
      },

      [dropdownOptions.REJECT_ALL]: () => ({
        acceptedQuantity: 0,
        returnQuantity: batch.totalQuantity,
        swapQuantity: undefined,
        stockDays: 0,
        option: 'REJECT_ALL',
      }),

      [dropdownOptions.PARTIAL]: () => ({
        acceptedQuantity: 0,
        returnQuantity: undefined,
        option: 'PARTIAL',
        stockDays: 0,
        acceptDamage: true,
      }),

      [dropdownOptions.ACCEPT_MAXIMUM]: () => {
        const PREC = 1000
        const boxInt = Math.round(batchState.quantityPerBox * PREC)
        const damageInt = Math.round(batch.damageQuantity * PREC)
        const returnBoxes = boxInt > 0 ? Math.ceil(damageInt / boxInt) : 0
        const returnQty = Math.round(returnBoxes * boxInt) / PREC
        const acceptedQty = Math.max(0, Math.round(batch.totalQuantity * PREC - returnBoxes * boxInt) / PREC)
        return {
          acceptedQuantity: acceptedQty,
          returnQuantity: returnQty,
          option: 'ACCEPT_MAXIMUM',
          acceptDamage: true,
          stockDays: salesPerDay !== 0 ? acceptedQty / salesPerDay : 0,
        }
      },
    }

    const value = optionResult[newOption]()

    dispatch(actions.updateBatchState({ [batch.idsBatchItemOrder.join(',')]: { ...batchState, ...value } }))
    handleErrorQuantity(value.acceptedQuantity!, newOption)
  }, [
    dispatch,
    idReceivementAuthorization,
    isAllOrNothingSupplier,
    batch.idsBatchItemOrder,
    batch.usefulQuantity,
    batch.damageQuantity,
    batch.totalQuantity,
    batchState,
    salesPerDay,
    handleErrorQuantity,
  ])

  const byPassConfirmDecision = useMemo(() => {
    if (!salesOverview?.products) return false
    return salesOverview.products.filter(
      (product) => product.idReceivementAuthorization === idReceivementAuthorization,
    ).length > 1
  }, [salesOverview?.products, idReceivementAuthorization])

  const handleOptionNotAllowInvoiceReturn = React.useCallback((event) => {
    const newOption = event.target.value as SalesDropdownOptions
    if (newOption) {
      setSelectedAction(newOption)
      setOpenDecisionConfirmationModal(true)
    }
  }, [])

  const handleChangeOptionNotAllowInvoiceReturn = React.useCallback(
    (newOption: string) => {
      dispatch(
        actions.setActionForAllProducts({
          idReceivementAuthorization,
          option: newOption,
        }),
      )
      closeConfirmationModal()
    },
    [dispatch, idReceivementAuthorization],
  )

  React.useEffect(() => {
    if (ref.current) return
    if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
      && (!salesOverview?.allowPartialReturn)
    ) {
      dispatch(actions.setActionForAllProductsInAr({ idReceivementAuthorization }))
    } else if (
      productStatus === ProductStatusEnum.CORRECT
      && batch.measureUnit.isKg
      && batch.damageQuantity > 0
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
      && (salesOverview?.allowInvoiceReturn && salesOverview?.allowPartialReturn)
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: batch.totalQuantity,
            option: 'ACCEPT_ALL_AND_DAMAGED',
            acceptDamage: true,
            stockDays: salesPerDay !== 0 ? batch.totalQuantity / salesPerDay : 0,
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
      && batch.usefulQuantity === 0
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: 0,
            returnQuantity: batch.totalQuantity,
            option: 'REJECT_ALL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
    ) {
      // "Aceitar sem avarias" (default) aceita todo o util sem clampar; se ficar
      // acima da AR, a validacao do produto exibe erro (igual a entrada manual).
      const accepted = batch.usefulQuantity
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: accepted,
            returnQuantity: batch.totalQuantity - accepted,
            option: 'ACCEPT_ALL',
            stockDays: salesPerDay !== 0 ? accepted / salesPerDay : 0,
          },
        }),
      )
    } else if (isProductNotReceived && !batchState.option) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: 0,
            option: 'REJECT_ALL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.DIVERGENT
      && batch.damageType === 'VALIDADE'
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
      && batch.usefulQuantity > 0
    ) {
      const accepted = clampAcceptedToAr(batch.usefulQuantity, remainingArBudget)
      const isClamped = Math.round(accepted * 1000) < Math.round(batch.usefulQuantity * 1000)
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: accepted,
            ...(isClamped ? { returnQuantity: batch.totalQuantity - accepted } : {}),
            option: 'PARTIAL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.DIVERGENT
      && !batchState.option
      && !(batchState.acceptedQuantity > 0)
      && batch.usefulQuantity === 0
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            returnQuantity: batch.totalQuantity,
            acceptedQuantity: 0,
            option: 'REJECT_ALL',
          },
        }),
      )
    }

    ref.current = true
  }, [
    batch.acceptedQuantity,
    batch.usefulQuantity,
    batch.selectedOption,
    batch.damageType,
    batch.totalQuantity,
    batch.expirationDate,
    productStatus,
    salesPerDay,
    isProductNotReceived,
    batchState,
    batch.idsBatchItemOrder,
    dispatch,
    salesOverview?.allowInvoiceReturn,
    salesOverview?.allowPartialReturn,
    idReceivementAuthorization,
    remainingArBudget,
    batch.measureUnit.isKg,
    batch.damageQuantity,
  ])

  const handleChange = useMemo(() => {
    if (!salesOverview?.allowInvoiceReturn && byPassConfirmDecision) {
      return handleOptionNotAllowInvoiceReturn
    }
    if (salesOverview?.allowInvoiceReturn && !salesOverview?.allowPartialReturn && byPassConfirmDecision) {
      return handleOptionNotAllowInvoiceReturn
    }
    return handleOption
  }, [
    byPassConfirmDecision,
    salesOverview?.allowInvoiceReturn,
    salesOverview?.allowPartialReturn,
    handleOptionNotAllowInvoiceReturn,
    handleOption,
  ])

  const handleSupplierMessage = () => {
    let conditionalMessage = 'Esse fornecedor não aceita notas de devolução'
    if (salesOverview?.allowInvoiceReturn && !salesOverview?.allowPartialReturn) conditionalMessage = `${conditionalMessage} parcial`

    return (
      <>
        <p>
          {conditionalMessage}, a ação <strong>{`${selectedAction?.toUpperCase()}`}</strong> será espelhada
          para todos os itens da nota <strong>{`${invoiceNumber}`}</strong>.
        </p>
        <p>
          Deseja prosseguir?
        </p>
      </>
    )
  }

  return (
    <>
      <FieldDropdown
        data-testid='type-input'
        value={
          !batch.selectedOption
            ? (batchState.option ? dropdownOptions[batchState.option] : '')
            : batch.selectedOption
        }
        disabled={isProductNotReceived || productIsRejected}
        options={options}
        onChange={handleChange}
        containerStyle={{ padding: 0 }}
        style={{
          border: 0,
          height: 22,
          backgroundColor: 'transparent',
          padding: 0,
          boxShadow: 'none',
          fontFamily: 'Work Sans',
          color: 'inherit',
          fontSize: 14,
        }}
      />

      {
        selectedAction
        && (
        <ConfirmOrRejectModal
          show={openDecisionConfirmationModal}
          title='Confirmação de decisão'
          customizableDescription={(
            <S.ContainerDescription>
              {handleSupplierMessage()}
            </S.ContainerDescription>
          )}
          onConfirm={() => handleChangeOptionNotAllowInvoiceReturn(selectedAction)}
          onReject={closeConfirmationModal}
          onClose={closeConfirmationModal}
          dataTestId='modal-confirmation-spread-option'
        />
        )

      }
    </>
  )
})

export { ActionDropdown, dropdownOptions }
